import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtNum } from "../lib/api";
import { PageHeader, Surface, Btn, Field, Input } from "../components/Primitives";
import { Plus, X, EnvelopeSimple, CheckCircle, WarningCircle, XCircle } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const DELIVERY_BADGES = {
  sent:        { label: "Sent",      cls: "text-zinc-400",   Icon: EnvelopeSimple },
  delivered:   { label: "Delivered", cls: "text-green-400",  Icon: CheckCircle },
  opened:      { label: "Opened",    cls: "text-green-400",  Icon: CheckCircle },
  clicked:     { label: "Clicked",   cls: "text-green-400",  Icon: CheckCircle },
  delayed:     { label: "Delayed",   cls: "text-amber",      Icon: WarningCircle },
  bounced:     { label: "Bounced",   cls: "text-red-400",    Icon: XCircle },
  complained:  { label: "Complained", cls: "text-red-400",   Icon: XCircle },
  failed:      { label: "Failed",    cls: "text-red-400",    Icon: XCircle },
};

export default function Participants() {
  const { user: currentUser } = useAuth();
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [editErr, setEditErr] = useState("");
  const [pwEdit, setPwEdit] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "", license_number: "", medical_expiry: "", password: "" });
  const [err, setErr] = useState("");

  const load = () => api.get("/participants").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setEditErr("");
    try {
      const { password_hash: _a, last_invite_email_id: _b, last_invite_status: _c,
              last_invite_event_at: _d, last_invite_event_type: _e, ...payload } = edit; // eslint-disable-line no-unused-vars
      await api.patch(`/participants/${edit.user_id}`, payload);
      if (pwEdit && pwEdit.length >= 10) {
        await api.post("/auth/password/reset", { user_id: edit.user_id, new_password: pwEdit });
      }
      setEdit(null); setPwEdit(""); load();
    } catch (e) {
      setEditErr(e.response?.data?.detail || "Save failed");
    }
  };

  const removeParticipant = async (p) => {
    if (!window.confirm(`Permanently delete ${p.name} (${p.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/participants/${p.user_id}`);
      setEdit(null); setPwEdit(""); setEditErr(""); load();
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    }
  };

  const resendInvite = async (p) => {
    if (!window.confirm(`Resend invite email to ${p.email}?`)) return;
    try {
      const r = await api.post(`/participants/${p.user_id}/resend-invite`);
      alert(r.data?.ok ? "Invite email queued." : "Invite could not be sent — check Settings → Outbound email.");
    } catch (e) {
      alert(e.response?.data?.detail || "Resend failed");
    }
  };

  const toggleStatus = async (p) => {
    const next = (p.status || "active") === "disabled" ? "active" : "disabled";
    const verb = next === "disabled" ? "disable" : "re-enable";
    if (!window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} ${p.name}? ${next === "disabled" ? "All active sessions will be revoked." : ""}`)) return;
    await api.patch(`/participants/${p.user_id}`, { status: next });
    load();
  };

  const submitInvite = async () => {
    setErr("");
    try {
      const body = { ...invite };
      if (!body.password) delete body.password;
      await api.post("/participants", body);
      setInviteOpen(false);
      setInvite({ email: "", name: "", license_number: "", medical_expiry: "", password: "" });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to invite");
    }
  };

  return (
    <div>
      <PageHeader overline="Admin" title="Participants"
        subtitle="People in the private group. Personal docs (medical) tracked here. Aircraft membership and named-insurance are managed per aircraft."
        actions={<Btn data-testid="invite-participant-btn" onClick={() => setInviteOpen(true)}><Plus size={14} className="inline mr-1.5" />Invite participant</Btn>}
      />
      <div className="p-8">
        <Surface>
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              <tr>
                <th className="text-left py-3 px-4">Name</th><th className="text-left py-3 px-4">Email</th>
                <th className="text-left py-3 px-4">Role</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-left py-3 px-4">Medical exp</th>
                <th className="text-right py-3 px-4">Hours</th><th className="text-right py-3 px-4 w-56">Action</th>
              </tr>
            </thead>
            <tbody data-testid="participants-table">
              {list.map((p) => {
                const isDisabled = (p.status || "active") === "disabled";
                const isSelf = p.user_id === currentUser?.user_id;
                return (
                  <tr key={p.user_id} className={`border-b border-zinc-900 hover:bg-[#1F1F1F] ${isDisabled ? "opacity-60" : ""}`} data-testid={`participant-row-${p.user_id}`}>
                    <td className="py-2.5 px-4">{p.name}</td>
                    <td className="py-2.5 px-4 text-zinc-400 font-mono text-xs">{p.email}</td>
                    <td className="py-2.5 px-4 uppercase tracking-wider text-xs text-zinc-300">{p.role}</td>
                    <td className="py-2.5 px-4">
                      {isDisabled ? (
                        <span className="inline-flex items-center gap-1 text-xs text-red-400 font-mono uppercase tracking-wider" data-testid={`participant-status-${p.user_id}`}>disabled</span>
                      ) : p.invited && !p.picture ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center gap-1 text-xs text-amber font-mono uppercase tracking-wider" data-testid={`participant-status-${p.user_id}`}><EnvelopeSimple size={12} /> invited</span>
                          {p.last_invite_status && DELIVERY_BADGES[p.last_invite_status] && (() => {
                            const b = DELIVERY_BADGES[p.last_invite_status];
                            const Ico = b.Icon;
                            return (
                              <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider ${b.cls}`} data-testid={`invite-delivery-${p.user_id}`} title={`last event ${p.last_invite_event_type || ""}`}>
                                <Ico size={10} weight="fill" /> {b.label}
                              </span>
                            );
                          })()}
                        </div>
                      ) : <span className="text-xs text-green-400 uppercase tracking-wider font-mono" data-testid={`participant-status-${p.user_id}`}>active</span>}
                    </td>
                    <td className="py-2.5 px-4 font-mono text-zinc-300">{fmtDate(p.medical_expiry)}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{fmtNum(p.total_hours, 1)}</td>
                    <td className="py-2.5 px-4 text-right">
                      <div className="inline-flex gap-2">
                        <Btn variant="ghost" onClick={() => setEdit({ ...p })} data-testid={`edit-participant-${p.user_id}`}>Manage</Btn>
                        {p.invited && !p.picture && !isDisabled && (
                          <Btn
                            variant="ghost"
                            onClick={() => resendInvite(p)}
                            data-testid={`resend-invite-${p.user_id}`}
                            className="text-amber hover:text-amber-300"
                          >
                            Resend invite
                          </Btn>
                        )}
                        {!isSelf && (
                          <Btn
                            variant="ghost"
                            onClick={() => toggleStatus(p)}
                            data-testid={`toggle-status-${p.user_id}`}
                            className={isDisabled ? "text-green-400 hover:text-green-300" : "text-red-400 hover:text-red-300"}
                          >
                            {isDisabled ? "Re-enable" : "Disable"}
                          </Btn>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Surface>
      </div>

      {inviteOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="invite-modal">
          <Surface className="w-full max-w-xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium">Invite participant</h3>
              <button onClick={() => setInviteOpen(false)}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="text-sm text-zinc-400 mb-6 leading-relaxed">
              Pre-create a participant in the private group. They can sign in with their Google email, or — if they provide an initial password — with email + password (for Apple users or anyone without Gmail). Add them to specific aircraft via each aircraft&apos;s <span className="amber">Members</span> tab.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Full name"><Input data-testid="invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></Field>
              <Field label="Email"><Input data-testid="invite-email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></Field>
              <Field label="License number"><Input value={invite.license_number} onChange={(e) => setInvite({ ...invite, license_number: e.target.value })} /></Field>
              <Field label="Medical expiry"><Input type="date" value={invite.medical_expiry} onChange={(e) => setInvite({ ...invite, medical_expiry: e.target.value })} /></Field>
              <div className="col-span-2">
                <Field label="Initial password (optional, for non-Google users)">
                  <Input data-testid="invite-password" type="password" autoComplete="new-password"
                    value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })}
                    placeholder="Leave empty if pilot signs in with Google" />
                </Field>
                <div className="text-[10px] text-zinc-500 font-mono mt-1">Min 10 chars, at least one letter + one digit. Pilot can change it after first login.</div>
              </div>
            </div>
            {err && <div className="mt-4 text-sm text-red-400 font-mono" data-testid="invite-error">{err}</div>}
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Btn>
              <Btn onClick={submitInvite} data-testid="invite-submit-btn">Send invite</Btn>
            </div>
          </Surface>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="edit-participant-modal">
          <Surface className="w-full max-w-2xl p-8">
            <h3 className="text-xl font-medium mb-6">Manage participant — {edit.name}</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Full name">
                <Input data-testid="edit-name" value={edit.name || ""}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              </Field>
              <Field label="Email">
                <Input
                  data-testid="edit-email"
                  type="email"
                  value={edit.email || ""}
                  disabled={!!edit.picture || !!edit.password_set_at}
                  onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                />
                {(edit.picture || edit.password_set_at) && (
                  <div className="text-[10px] text-zinc-500 font-mono mt-1">Email locked — pilot has already signed in.</div>
                )}
              </Field>
              <Field label="Role">
                <select data-testid="edit-role" className="w-full h-10 bg-[#0A0A0A] border border-zinc-800 px-3 text-sm" value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
                  <option value="participant">participant</option>
                  <option value="owner">owner</option>
                </select>
              </Field>
              <Field label="License number"><Input value={edit.license_number || ""} onChange={(e) => setEdit({ ...edit, license_number: e.target.value })} /></Field>
              <Field label="Medical expiry"><Input type="date" value={edit.medical_expiry || ""} onChange={(e) => setEdit({ ...edit, medical_expiry: e.target.value })} /></Field>
              <Field label="Total hours"><Input type="number" step="0.1" value={edit.total_hours} onChange={(e) => setEdit({ ...edit, total_hours: parseFloat(e.target.value || 0) })} /></Field>
              <div className="col-span-2">
                <Field label="Reset password (optional)">
                  <Input data-testid="edit-password-reset" type="password" autoComplete="new-password"
                    value={pwEdit} onChange={(e) => setPwEdit(e.target.value)}
                    placeholder="Leave empty to keep existing password" />
                </Field>
                <div className="text-[10px] text-zinc-500 font-mono mt-1">Min 10 chars, letter + digit. Revokes all their active sessions on save.</div>
              </div>
            </div>
            {editErr && <div className="mt-4 text-sm text-red-400 font-mono" data-testid="edit-error">{editErr}</div>}
            <div className="flex justify-between items-center gap-2 mt-6">
              <Btn
                variant="ghost"
                onClick={() => removeParticipant(edit)}
                data-testid="delete-participant-btn"
                className="text-red-400 hover:text-red-300"
              >
                Delete participant
              </Btn>
              <div className="flex gap-2">
                <Btn variant="ghost" onClick={() => { setEdit(null); setPwEdit(""); setEditErr(""); }}>Cancel</Btn>
                <Btn onClick={save} data-testid="save-participant-btn">Save</Btn>
              </div>
            </div>
          </Surface>
        </div>
      )}
    </div>
  );
}
